import type { PlatformStatus } from "./dicts";
import { t } from "./i18n-platform-overview";

export type PlatformHealthProbeErrorCode =
  | "platform.admin.probe.url_unavailable"
  | "platform.admin.probe.http_error"
  | "platform.admin.probe.timeout"
  | "platform.admin.probe.request_aborted"
  | "platform.admin.probe.network_unreachable"
  | "platform.admin.probe.request_failed";

export type PlatformHealthProbeErrorParams = Record<string, string | number | boolean>;

export type PlatformHealthItem = {
  key: string;
  label: string;
  status: PlatformStatus;
  http_status: number | null;
  latency_ms: number | null;
  checked_at: string;
  error: string | null;
  error_code: PlatformHealthProbeErrorCode | null;
  error_params: PlatformHealthProbeErrorParams | null;
};

export type PlatformHealthPayload = {
  overall_status: PlatformStatus;
  items: PlatformHealthItem[];
};

export type SharedHealthProbeSpec = {
  key: string;
  label: string;
  url: string | undefined;
  includeInOverall: boolean | undefined;
  probeMode: "cors" | "reachable" | undefined;
};

const DEFAULT_HTTP_TIMEOUT_MS = 4000;

function createTimeoutSignal(timeoutMs: number) {
  const controller = new AbortController();
  let timedOut = false;
  const timer = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  return {
    signal: controller.signal,
    clear: () => window.clearTimeout(timer),
    didTimeout: () => timedOut,
  };
}

export async function probePlatformHealth(
  spec: SharedHealthProbeSpec,
): Promise<PlatformHealthItem> {
  if (!spec.url || typeof window === "undefined") {
    return {
      key: spec.key,
      label: spec.label,
      status: "unknown",
      http_status: null,
      latency_ms: null,
      checked_at: new Date().toISOString(),
      error: "health url unavailable",
      error_code: "platform.admin.probe.url_unavailable",
      error_params: null,
    };
  }

  const startedAt = Date.now();
  const { signal, clear, didTimeout } = createTimeoutSignal(DEFAULT_HTTP_TIMEOUT_MS);
  try {
    const probeMode = spec.probeMode ?? "cors";
    const response = await fetch(spec.url, {
      method: "GET",
      credentials: probeMode === "reachable" ? "omit" : "include",
      mode: probeMode === "reachable" ? "no-cors" : "cors",
      signal,
    });
    const latencyMs = Date.now() - startedAt;
    if (probeMode === "reachable") {
      return {
        key: spec.key,
        label: spec.label,
        status: "online",
        http_status: null,
        latency_ms: latencyMs,
        checked_at: new Date().toISOString(),
        error: null,
        error_code: null,
        error_params: null,
      };
    }
    const status = response.ok ? "online" : response.status >= 500 ? "offline" : "degraded";
    return {
      key: spec.key,
      label: spec.label,
      status,
      http_status: response.status,
      latency_ms: latencyMs,
      checked_at: new Date().toISOString(),
      error: response.ok ? null : `http ${response.status}`,
      error_code: response.ok ? null : "platform.admin.probe.http_error",
      error_params: response.ok ? null : { status: response.status },
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : null;
    const errorCode: PlatformHealthProbeErrorCode = didTimeout()
      ? "platform.admin.probe.timeout"
      : error instanceof DOMException && error.name === "AbortError"
        ? "platform.admin.probe.request_aborted"
        : error instanceof TypeError
          ? "platform.admin.probe.network_unreachable"
          : "platform.admin.probe.request_failed";
    const errorParams =
      errorCode === "platform.admin.probe.timeout"
        ? ({ timeout_ms: DEFAULT_HTTP_TIMEOUT_MS } satisfies PlatformHealthProbeErrorParams)
        : null;
    return {
      key: spec.key,
      label: spec.label,
      status: "offline",
      http_status: null,
      latency_ms: Date.now() - startedAt,
      checked_at: new Date().toISOString(),
      error: errorMessage,
      error_code: errorCode,
      error_params: errorParams,
    };
  } finally {
    clear();
  }
}

export function formatPlatformHealthError(
  item: Pick<PlatformHealthItem, "error_code" | "error_params">,
) {
  switch (item.error_code) {
    case null:
      return t("platform.admin.common.latestCheckSuccess");
    case "platform.admin.probe.url_unavailable":
      return t("platform.admin.common.probeErrors.urlUnavailable");
    case "platform.admin.probe.http_error":
      return t("platform.admin.common.probeErrors.httpError", item.error_params ?? undefined);
    case "platform.admin.probe.timeout":
      return t("platform.admin.common.probeErrors.timeout", item.error_params ?? undefined);
    case "platform.admin.probe.request_aborted":
      return t("platform.admin.common.probeErrors.requestAborted");
    case "platform.admin.probe.network_unreachable":
      return t("platform.admin.common.probeErrors.networkUnreachable");
    case "platform.admin.probe.request_failed":
      return t("platform.admin.common.probeErrors.requestFailed");
    default:
      return t("common.messages.requestFailed");
  }
}

export function deriveOverallPlatformHealthStatus(
  items: Array<Pick<PlatformHealthItem, "key" | "status">>,
  specs: Array<Pick<SharedHealthProbeSpec, "key" | "includeInOverall">>,
): PlatformStatus {
  const statuses = specs
    .filter((spec) => spec.includeInOverall !== false)
    .map((spec) => items.find((item) => item.key === spec.key)?.status ?? "offline");
  return statuses.includes("offline")
    ? "offline"
    : statuses.includes("degraded")
      ? "degraded"
      : statuses.includes("online")
        ? "online"
        : "unknown";
}
